import { ERROR_CODE, UniversalError, getErrorStream, toUniversalError } from "@packages/core";
import { t } from "i18next";
import { Fragment, type ReactNode } from "react";
import { LuCopyCheck, LuX } from "react-icons/lu";
import { ExternalToast, toast } from "sonner";

import { useGetErrorMessage, useTranslate } from "../../services/index";
import { css } from "../../styled-system/css";

getErrorStream().subscribe({
  complete() {
    showError(
      new UniversalError(ERROR_CODE.UNEXPECTED, {
        message: "エラーのストリームが意図せず完了しました。",
      }),
    );
  },
  error(error) {
    showError(toUniversalError(error, { message: "エラーのストリームでエラーが発生しました。" }));
  },
  next(error) {
    showError(error);
  },
});

export async function showMessage(
  parameters: {
    body: ReactNode;
    color: "success" | "error" | "information";
    icon: ReactNode;
    title: ReactNode;
    type: "dialog" | "toast";
  },
  options?: ExternalToast,
) {
  toast.custom(
    function (id) {
      return (
        <Toast
          body={parameters.body}
          color={parameters.color}
          icon={parameters.icon}
          id={id}
          title={parameters.title}
        />
      );
    },
    {
      onAutoClose(toast) {
        console.debug("メッセージが自動的に閉じられました。", toast);
      },
      onDismiss(toast) {
        console.debug("メッセージが手動で閉じられました。", toast);
      },
      duration: 5000,
      ...options,
    },
  );
}

interface ToastProperties {
  body: ReactNode;
  color: "success" | "error" | "information";
  icon: ReactNode;
  id: string | number;
  title: ReactNode;
}

function Toast({ body, color, icon, id, title }: ToastProperties) {
  return (
    <div
      className={css(
        {
          alignItems: "center",
          borderRadius: "8px",
          display: "flex",
          gap: "8px",
          paddingX: "16px",
          paddingY: "22px",
        },
        color === "error" && {
          backgroundColor: "destructive",
          color: "destructive-foreground",
        },
        color === "success" && {
          backgroundColor: "success",
          color: "success-foreground",
        },
      )}
    >
      <div
        className={css({
          height: "24px",
          width: "24px",
          "& > *": { height: "24px", width: "24px" },
          _empty: { display: "none" },
        })}
      >
        {icon}
      </div>
      <div className={css({ alignItems: "center", display: "flex", flex: 1, gap: "8px" })}>
        <div className={css({ width: "100%" })}>
          <p className={css({ fontSize: "14px", fontWeight: 500, whiteSpace: "pre-wrap" })}>
            {title}
          </p>
          <p className={css({ fontSize: "10px", fontWeight: 400, whiteSpace: "pre-wrap" })}>
            {body}
          </p>
        </div>
      </div>
      <div>
        <LuX
          className={css({
            cursor: "pointer",
            height: "24px",
            width: "24px",
            _hover: { opacity: 0.8 },
            _active: { opacity: 0.6 },
          })}
          onClick={function (event) {
            event.stopPropagation();
            event.preventDefault();
            toast.dismiss(id);
          }}
        />
      </div>
    </div>
  );
}

// =============================================================
// エラーに関するトースト
// =============================================================

/** @deprecated 意図せず標準関数と名前が重複しているので代わりに`showError`を使用する。 */
export const reportError = showError;

export async function showError(error: any, options?: ExternalToast) {
  console.warn("エラーが発生しました。", error);

  const universalError = toUniversalError(error);

  toast.custom(
    function (id) {
      return <ErrorToast error={universalError} id={id} />;
    },
    {
      onAutoClose(toast) {
        console.debug("エラーが自動的に閉じられました。", toast);
      },
      onDismiss(toast) {
        console.debug("エラーが手動で閉じられました。", toast);
      },
      duration: 5000,
      ...options,
    },
  );

  return universalError;
}

interface ErrorToastProperties {
  error: Error;
  id: string | number;
}

function ErrorToast({ error, id }: ErrorToastProperties) {
  const getErrorMessage = useGetErrorMessage();
  const translate = useTranslate();

  return (
    <Toast
      body={error instanceof UniversalError ? getErrorMessage(error) : error.message}
      color="error"
      icon={<LuX />}
      id={id}
      title={
        error instanceof UniversalError ? (
          <Fragment>
            {translate("%Error.0%")}&nbsp;:&nbsp;{error.code}
          </Fragment>
        ) : (
          translate("%Error.0%")
        )
      }
    />
  );
}

// =============================================================
// クリップボードにコピーする関数
// =============================================================

export async function copyToClipboard(text: string) {
  if (!navigator.clipboard) {
    await showError(new UniversalError(ERROR_CODE.CLIPBOARD_UNAVAILABLE));
    return;
  }

  try {
    await navigator.clipboard.writeText(text);
    await showMessage({
      body: text,
      color: "success",
      icon: <LuCopyCheck />,
      title: t("%1%"),
      type: "toast",
    });
  } catch (error) {
    console.error("クリップボードへのコピーに失敗しました。");
    await showError(
      new UniversalError(ERROR_CODE.COPY_TO_CLIPBOARD_FAILED, {
        cause: error,
        data: { public: { text } },
      }),
    );
  }
}
